import React from "react";
import { Helmet } from "react-helmet";
import homebackground from './../image/banner.png';

const ScienceSales = () => {
    return (
        <div className="w-full h-auto bg-white flex flex-col items-center justify-center lg:mt-20 sm:mt-0">
            <Helmet>
                <title>Science Sales - Nexify Infosystems</title>
                <meta name="description" content="Explore the science of sales, understanding buyer behavior, data-driven decisions, and optimizing sales processes for success." />
                <meta name="keywords" content="Science Sales, Sales, Buyer Behavior, Data-Driven Sales, Sales Process, Sales Optimization, Behavioral Science, Sales Careers" />
            </Helmet>
            <div
                className="w-full h-[440px] bg-cover bg-center relative flex items-center justify-center"
                style={{ backgroundImage: `url(${homebackground})` }}
            >
                <div className="relative z-0 flex justify-center lg:justify-start items-center w-full px-4 sm:px-10 lg:px-20">
                    <div
                        className="opacity-0 translate-y-10 animate-slideUp bg-[#0c5495b3] p-6 shadow-lg text-white inline-block text-center lg:text-left max-w-[90%] sm:max-w-[80%] md:max-w-[60%] lg:max-w-[60%] lg:ml-14"
                        style={{
                            animation: "slideUp 1s ease-in-out forwards",
                            animationDelay: "2s",
                        }}
                    >
                        <h1 className="text-[8vw] md:text-[66px] font-semibold mb-2 break-words">
                            Nexify Science Sales
                        </h1>
                        <h2 className="text-[5vw] md:text-[40px] text-[#efefef]">
                            Empowering innovation, connecting talent, driving growth.
                        </h2>
                    </div>
                </div>
            </div>
            <div className="w-full max-w-[1320px] px-4 mx-auto mt-20 mb-20">

                <h2 className="mb-6 text-[#0e55a8] lg:text-[40px] text-[30px] leading-[1.8] font-[700] text-left">Science Sales</h2>

                {/* Overview Section */}
                <div className="mt-8">
                    <p className="text-[18px] text-[#222] font-[400] leading-[1.8] mb-6">
                        The science of sales uses data, psychology, and proven methods to optimize processes, enhance effectiveness, and drive revenue growth.
                    </p>

                    <h3 className="text-[16px] font-[700] text-[#222] mb-4">
                        1. Understanding Buyer Behavior – <span className="text-[16px] text-[#6d6d70] font-[400]">
                            Leverage psychology and customer journey mapping to align sales strategies.
                        </span>
                    </h3>

                    <h3 className="text-[16px] font-[700] text-[#222] mb-4">
                        2. Data-Driven Decision-Making – <span className="text-[16px] text-[#6d6d70] font-[400]">
                            Use analytics, predictive modeling, and CRM systems to refine sales approaches.
                        </span>
                    </h3>

                    <h3 className="text-[16px] font-[700] text-[#222] mb-4">
                        3. Behavioral Science in Selling – <span className="text-[16px] text-[#6d6d70] font-[400]">
                            Build rapport, use persuasion techniques, and address customer pain points.
                        </span>
                    </h3>

                    <h3 className="text-[16px] font-[700] text-[#222] mb-4">
                        4. Sales Process Optimization – <span className="text-[16px] text-[#6d6d70] font-[400]">
                            Implement structured methodologies, test strategies, and prioritize high-value leads.
                        </span>
                    </h3>

                    <h3 className="text-[16px] font-[700] text-[#222] mb-4">
                        5. Technology and Tools – <span className="text-[16px] text-[#6d6d70] font-[400]">
                            Automate tasks, utilize AI for insights, and leverage sales enablement platforms.
                        </span>
                    </h3>

                    <h3 className="text-[16px] font-[700] text-[#222] mb-4">
                        6. Continual Learning and Adaptation – <span className="text-[16px] text-[#6d6d70] font-[400]">
                            Apply A/B testing, monitor market trends, and develop key sales skills.
                        </span>
                    </h3>

                    <h3 className="text-[16px] font-[700] text-[#222] mb-4">
                        7. The Role of Feedback – <span className="text-[16px] text-[#6d6d70] font-[400]">
                            Use customer insights and team collaboration to improve sales effectiveness.
                        </span>
                    </h3>
                    <p className="text-[16px] text-[#6d6d70] leading-[1.8] mb-6">
                        The science of sales ensures that selling is not based solely on intuition or charm but is rooted in measurable, repeatable practices. By combining the art of relationship-building with the rigor of data and behavioral insights, sales professionals can achieve consistent, scalable success.
                    </p>

                    {/* Sources Section */}

                    <div className=" bg-white">
                        <h2 className="text-[30px] font-[700] text-[#0e55a8] mb-4">Key Sources and Areas of Knowledge</h2>

                        <div className="mb-6">
                            <h3 className="text-[20px] font-[700] text-[#222] mb-2">1. Academic Research & Behavioral Science</h3>
                            <p className="text-[#6d6d70] text-[400] text-[16px]">
                                <strong className="text-gray-700">Books:</strong> Influence (Cialdini), Thinking, Fast and Slow (Kahneman), SPIN Selling (Rackham).<br />
                                <strong className="text-gray-700">Research:</strong> Studies by Kahneman & Tversky, journals like Harvard Business Review.
                            </p>
                        </div>

                        <div className="mb-6">
                            <h3 className="text-[20px] font-[700] text-[#222] mb-2">2. Sales Methodologies</h3>
                            <p className="text-[#6d6d70] text-[400] text-[16px]">
                                <strong className="text-gray-700">Frameworks:</strong> SPIN Selling, Challenger Sale, Solution Selling.<br />
                                <strong className="text-gray-700">Books:</strong> The Challenger Sale (Dixon & Adamson), The Sandler Rules (Mattson).
                            </p>
                        </div>

                        <div className="mb-6">
                            <h3 className="text-[20px] font-[700] text-[#222] mb-2">3. Sales Tools & Technology</h3>
                            <p className="text-[#6d6d70] text-[400] text-[16px]">
                                <strong className="text-gray-700">CRM:</strong> Salesforce, HubSpot, Zoho CRM.<br />
                                <strong className="text-gray-700">Analytics:</strong> Tableau, Power BI.<br />
                                <strong className="text-gray-700">AI in Sales:</strong> Gong.io, Chorus.ai for conversation analytics.
                            </p>
                        </div>

                        <div className="mb-6">
                            <h3 className="text-[20px] font-[700] text-[#222] mb-2">4. Industry Insights</h3>
                            <p className="text-[#6d6d70] text-[400] text-[16px]">
                                <strong className="text-gray-700">Reports:</strong> Gartner Sales Trends, McKinsey & Company insights.<br />
                                <strong className="text-gray-700">Publications:</strong> Forbes, Inc., Harvard Business Review, HubSpot’s sales blog.
                            </p>
                        </div>

                        <div className="mb-6">
                            <h3 className="text-[20px] font-[700] text-[#222] mb-2">5. Professional Development</h3>
                            <p className="text-[#6d6d70] text-[400] text-[16px]">
                                <strong className="text-gray-700">Certifications:</strong> HubSpot Sales, Salesforce Sales Cloud Consultant.<br />
                                <strong className="text-gray-700">Conferences:</strong> INBOUND, Sales Enablement Summit.<br />
                                <strong className="text-gray-700">Communities:</strong> LinkedIn Sales Groups, Reddit’s r/sales.
                            </p>
                        </div>

                        <div className="mb-6">
                            <h3 className="text-[20px] font-[700] text-[#222] mb-2">6. Case Studies & Best Practices</h3>
                            <p className="text-[#6d6d70] text-[400] text-[16px] ">
                                <strong className="text-gray-700">Successful Models:</strong> Amazon (data-driven selling), Apple (experience-focused sales), SaaS leaders (Slack, Zoom).
                            </p>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default ScienceSales;
